import type { CSSProperties, ReactElement } from "react";
import { AlertTriangle } from "lucide-react";
import { color, layout, radius, space } from "@tokens/index";
import { Button, SkeletonBlock, Surface, Text } from "@primitives/index";
import type { TimelineSubjectKind } from "./types";

const skeletonDayStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 0,
  marginBottom: space[5],
};

/** Height of one placeholder row: the settled height of a two-line `TimelineRow`. */
export const TIMELINE_SKELETON_ROW_HEIGHT = 56;

/**
 * Day groups of event rows, so the placeholder keeps the feed's shape. Rows
 * hold the height of a real row, so the crossfade to content does not shift
 * layout.
 */
export function TimelineSkeleton(): ReactElement {
  return (
    <div role="status" aria-live="polite" aria-label="Loading timeline" aria-busy="true">
      {Array.from({ length: 3 }, (_, day) => (
        <div key={day} style={skeletonDayStyle} aria-hidden="true">
          <SkeletonBlock width={60} height={10} style={{ marginBlock: space[2] }} />
          {Array.from({ length: 3 }, (_, row) => (
            <div
              key={row}
              style={{
                display: "flex",
                gap: space[3],
                alignItems: "center",
                height: TIMELINE_SKELETON_ROW_HEIGHT,
                paddingInline: space[1],
                boxSizing: "border-box",
              }}
            >
              <SkeletonBlock width={32} height={32} radius="full" />
              <SkeletonBlock height={10} style={{ flex: 1, width: "auto" }} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

const stateLineStyle: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: space[3],
  paddingBlock: space[4],
  paddingInline: space[1],
  maxWidth: layout.proseMaxWidth,
};

export function ErrorState({
  error,
  onRetry,
}: {
  readonly error: Error;
  readonly onRetry?: (() => void) | undefined;
}): ReactElement {
  return (
    <Surface elevation={1} padding={4} borderRadius="lg" role="alert">
      <div style={{ display: "flex", gap: space[3], alignItems: "flex-start", minWidth: 0 }}>
        <AlertTriangle
          size={20}
          strokeWidth={2}
          color={color.accent.danger}
          aria-hidden="true"
          style={{ flexShrink: 0, marginTop: "2px" }}
        />
        <div style={{ display: "flex", flexDirection: "column", gap: space[1], minWidth: 0 }}>
          <Text role="body" weight="semibold" tone="danger">
            Timeline unavailable
          </Text>
          <Text role="bodySm" tone="muted" style={{ overflowWrap: "anywhere" }}>
            The activity ledger could not be reached. {error.message}
          </Text>
          {onRetry !== undefined ? (
            <div style={{ marginTop: space[2] }}>
              <Button variant="secondary" size="md" onClick={onRetry}>
                Retry
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    </Surface>
  );
}

export function EmptyState({
  subjectKind,
}: {
  readonly subjectKind: TimelineSubjectKind;
}): ReactElement {
  return (
    <div style={stateLineStyle}>
      <Text role="body" tone="muted">
        Nothing has been logged on this {subjectKind} yet.
      </Text>
    </div>
  );
}

export function FilteredEmptyState({ onClear }: { readonly onClear: () => void }): ReactElement {
  return (
    <div style={stateLineStyle}>
      <Text role="body" tone="muted">
        No activity matches your filters
      </Text>
      <Button variant="secondary" size="md" onClick={onClear}>
        Clear filters
      </Button>
    </div>
  );
}

export function DisclosureBanner({
  truncated,
  partial,
  subjectKind,
}: {
  readonly truncated: boolean;
  readonly partial: boolean;
  readonly subjectKind: TimelineSubjectKind;
}): ReactElement {
  const messages: string[] = [];
  if (partial) {
    messages.push("Some events couldn't be loaded — the activity ledger had partial outages.");
  }
  if (truncated) {
    messages.push(`Older events are hidden — this ${subjectKind} exceeded the timeline cap.`);
  }
  return (
    <div
      role="note"
      style={{
        display: "flex",
        gap: space[3],
        alignItems: "center",
        padding: space[3],
        borderRadius: radius.md,
        background: color.status.warningBg,
        border: `1px solid ${color.border.subtle}`,
        minWidth: 0,
      }}
    >
      <AlertTriangle
        size={16}
        strokeWidth={2.25}
        color={color.status.warningFg}
        aria-hidden="true"
        style={{ flexShrink: 0 }}
      />
      <Text role="bodySm" tone="muted">
        {messages.join(" ")}
      </Text>
    </div>
  );
}
